/**
 * ICCID utilities for OutdoorLink BLE advertisement parsing and site matching.
 *
 * OutdoorLink units advertise their SIM ICCID in BLE manufacturer data under
 * company id 0x02FF (decimal 767). The payload is ASCII digits with the
 * leading "8910" industry/issuer prefix stripped; these helpers reconstruct
 * the full ICCID and match it against a site's stored ICCID.
 *
 * Contains:
 * - parseAdvertisedIccid — extract and reconstruct full ICCID from a ScanResult's manufacturerData
 * - iccidMatchesSite     — tolerant equality check between advertised and site-stored ICCIDs
 */

/** Company id assigned to OutdoorLink in BLE manufacturer data, as decimal string. */
const ODL_COMPANY_ID = '767';

/** Industry / issuer prefix that units strip before advertising. */
const ICCID_PREFIX = '8910';

/**
 * Parse the full ICCID from a BLE ScanResult's manufacturerData.
 *
 * The plugin delivers manufacturerData as a plain object keyed by the decimal
 * company-id string, with DataView values (e.g. `{ "767": DataView }`).
 * OutdoorLink units advertise ASCII digit bytes with the "8910" prefix
 * stripped; this function re-prepends that prefix to return the full ICCID.
 *
 * @param {Object<string, DataView> | null | undefined} manufacturerData
 * @returns {string | null} Full ICCID string, or null if the 0x02FF entry is absent or contains no digits.
 */
export function parseAdvertisedIccid(manufacturerData) {
  if (!manufacturerData) {
    return null;
  }

  const dataView = manufacturerData[ODL_COMPANY_ID];
  if (!dataView) {
    return null;
  }

  let digits = '';
  for (let i = 0; i < dataView.byteLength; i++) {
    const char = String.fromCharCode(dataView.getUint8(i));
    if (/\d/.test(char)) {
      digits += char;
    }
  }

  if (!digits) {
    return null;
  }

  return ICCID_PREFIX + digits;
}

/**
 * Check whether an advertised full ICCID corresponds to a site's stored ICCID.
 *
 * Tolerates prefix mismatches: matches when the values are exactly equal OR
 * when one is a suffix of the other (handles sites that store a stripped form).
 *
 * @param {string | null | undefined} advertised - Full ICCID from parseAdvertisedIccid.
 * @param {string | null | undefined} siteIccid  - ICCID stored on the site record.
 * @returns {boolean}
 */
export function iccidMatchesSite(advertised, siteIccid) {
  if (!advertised || !siteIccid) {
    return false;
  }

  if (advertised === siteIccid) {
    return true;
  }

  return advertised.endsWith(siteIccid) || siteIccid.endsWith(advertised);
}
